import { axiosBaseQuery } from '@client';
import { PDAExportResponse } from '@containers/common/da-tool/evaluation/BudgetStatus/GescoIntegrationInfo';
import {
  EvaluationAvailableToRelateProforma,
  EvaluationAvailableToRelateProformaResponse
} from '@module/evaluation/model/EvaluationAvailableToRelateProformaResponse';
import { EvaluationPaymentInstructionsForm } from '@module/evaluation/model/EvaluationPaymentInstructionsForm';
import { EvaluationResponse } from '@module/evaluation/model/EvaluationResponse';
import { SaveEvaluationValuesForm } from '@module/evaluation/model/SaveEvaluationValuesForm';
import { ExpenseEvaluationParametersForm } from '@module/expense-evaluation/model/ExpenseEvaluationParametersForm';
import { ExpenseEvaluationResponse } from '@module/expense-evaluation/model/ExpenseEvaluationResponse';
import { TaskResponseState } from '@module/integration/task/model/TaskResponse';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { ExpenseEvaluationVerifyForm } from '@state/types/ExpenseEvaluationVerifyForm';
import { PsUtilities } from 'components';
import {
  AdditionalPayment,
  ExpenseEvaluationBudgetStatusRequest,
  ExpenseEvaluationBudgetStatusResponse
} from '../../types';
import { addSelectedValueToLinkedPDAs, getSelectedPDAsIds } from './apiHelpers';
import { ExpenseEvaluationEditForm } from '@module/expense-evaluation/model/ExpenseEvaluationEditForm';
import { EvaluationAvailableChartererAccountItemsOverviewResponse } from '@module/evaluation/model/EvaluationAvailableChartererAccountItemsOverviewResponse';
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';

const expenseEvaluationEntityAdapter =
  createEntityAdapter<ExpenseEvaluationResponse>({
    selectId: ({ id }) => id
  });

export const { selectById: selectExpenseEvaluationById } =
  expenseEvaluationEntityAdapter.getSelectors();

export const evaluationApi = createApi({
  baseQuery: axiosBaseQuery({ baseUrl: 'secured/evaluation' }),
  tagTypes: ['EVALUATION', 'BudgetStatus', 'BudgetCommit'],
  reducerPath: 'evaluation-api',
  endpoints: (build) => ({
    verifyExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: number;
        formValues: ExpenseEvaluationVerifyForm;
        attachments?: {
          principalsAccount: File[];
          ownersAccount: File[];
          charterersAccount: File[];
        };
      }
    >({
      query: ({ evaluationId, formValues, attachments }) => {
        const formData = PsUtilities.createFormRequestParts(
          formValues,
          attachments?.principalsAccount ?? []
        );

        if (attachments) {
          PsUtilities.addAttachmentsToForm(
            formData,
            attachments.ownersAccount,
            'ownersAccountAttachments'
          );

          PsUtilities.addAttachmentsToForm(
            formData,
            attachments.charterersAccount,
            'charterersAccountAttachments'
          );
        }

        return {
          url: `/${evaluationId}/verify-expense-evaluation`,
          data: formData,
          method: 'POST',
          headers: {
            'Content-Type': 'multipart/form-data;'
          },
          formData: true
        };
      },
      invalidatesTags: (res, _err, { evaluationId }) =>
        res
          ? [
              { type: 'EVALUATION', evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    }),
    fetchLinkedPDAs: build.query<
      EvaluationAvailableToRelateProforma,
      { evaluationId: number; relationIds: Set<string> }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/load-available-to-relate-proformas`,
        method: 'GET'
      }),
      transformResponse: (
        response: EvaluationAvailableToRelateProformaResponse,
        meta,
        arg
      ) => addSelectedValueToLinkedPDAs(response, arg.relationIds)
    }),
    submitLinkedPDAs: build.mutation<
      EvaluationResponse,
      {
        evaluationFromData: Omit<SaveEvaluationValuesForm, 'relationIds'>;
        selectedLinkedPDAs: EvaluationAvailableToRelateProforma;
      }
    >({
      query: ({ evaluationFromData, selectedLinkedPDAs }) => {
        const saveEvaluationValuesForm: SaveEvaluationValuesForm = {
          id: evaluationFromData.id,
          currency: evaluationFromData.currency,
          rate: evaluationFromData.rate,
          terminalId: evaluationFromData.terminalId,
          berthId: evaluationFromData.berthId,
          invoiceNumber: evaluationFromData.invoiceNumber,
          invoiceDate: evaluationFromData.invoiceDate,
          relationIds: getSelectedPDAsIds(selectedLinkedPDAs)
        };

        return {
          url: '/save-evaluation-values',
          data: saveEvaluationValuesForm,
          method: 'POST'
        };
      },
      invalidatesTags: (res, _, { evaluationFromData }) =>
        res
          ? [{ type: 'EVALUATION', id: evaluationFromData.id }]
          : ['EVALUATION']
    }),

    fetchBudgetStatus: build.query<
      ExpenseEvaluationBudgetStatusResponse,
      { evaluationId: number | string }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/budget-check`,
        method: 'GET'
      }),
      providesTags: ['BudgetStatus']
    }),
    performBudgetCheck: build.mutation<
      ExpenseEvaluationBudgetStatusResponse,
      ExpenseEvaluationBudgetStatusRequest
    >({
      query: ({ evaluationId, ...data }) => ({
        url: `/${evaluationId}/budget-check`,
        data: {
          ...data,
          evaluationId: evaluationId
        },
        method: 'POST'
      }),
      async onQueryStarted({ evaluationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            evaluationApi.util.updateQueryData(
              'fetchBudgetStatus',
              { evaluationId },
              () => data
            )
          );
        } catch {
          //
        }
      },
      invalidatesTags: ['BudgetCommit']
    }),

    fetchBudgetCommitStatus: build.query<
      Map<string, PDAExportResponse>,
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/budget-commit`,
        method: 'GET'
      }),
      providesTags: (result, _error, arg) =>
        result
          ? [{ type: 'BudgetCommit' as const, id: arg.evaluationId }]
          : ['BudgetCommit']
    }),
    performBudgetCommit: build.mutation<
      Map<string, PDAExportResponse>,
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/budget-commit`,
        data: {
          evaluationId: evaluationId
        },
        method: 'POST'
      }),
      invalidatesTags: (result, _error, arg) =>
        result
          ? [{ type: 'BudgetCommit' as const, id: arg.evaluationId }]
          : ['BudgetCommit']
    }),
    sendBudgetCommitReminder: build.mutation<
      void,
      { evaluationId: number; departmentName: string }
    >({
      query: ({ evaluationId, departmentName }) => ({
        url: `/${evaluationId}/budget-commit/send-reminder/department/${departmentName}`,
        data: {
          evaluationId: evaluationId,
          departmentName: departmentName
        },
        method: 'POST'
      })
    }),

    performRetryTask: build.mutation<{ state: TaskResponseState }, number>({
      query: (evaluationId) => ({
        url: `/${evaluationId}/retry-task`,
        method: 'POST'
      }),
      invalidatesTags: ['BudgetStatus']
    }),

    performGetTask: build.query<TaskResponseState, number>({
      query: (evaluationId) => ({
        url: `/${evaluationId}/get-task`,
        method: 'GET'
      })
    }),

    savePaymentInstructions: build.mutation<
      EvaluationResponse,
      EvaluationPaymentInstructionsForm
    >({
      query: ({ ...data }) => {
        return {
          url: '/save-payment-instructions',
          data,
          method: 'POST'
        };
      }
    }),

    submitApr: build.mutation<AdditionalPayment, AdditionalPayment>({
      query: ({ ...data }) => {
        return {
          url: '/apr-submit',
          data,
          method: 'POST'
        };
      }
    }),

    loadEvaluation: build.query<
      EvaluationResponse & {
        evaluationExpensesEntities: EntityState<ExpenseEvaluationResponse>;
      },
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/load`,
        method: 'GET'
      }),

      transformResponse: (evaluationResponse: EvaluationResponse) => {
        const expenseEvaluations: ExpenseEvaluationResponse[] = [
          ...(evaluationResponse.portExpensesSection?.expenseEvaluations ?? []),
          ...(evaluationResponse.agencyExpensesSection?.expenseEvaluations ??
            []),
          ...(evaluationResponse.husbandryExpensesSection?.expenseEvaluations ??
            []),
          ...(
            evaluationResponse.agencyExpensesSection?.expenseEvaluations ?? []
          ).reduce<ExpenseEvaluationResponse[]>((acc, cur) => {
            acc.push(...(cur.editExpenses ?? []), ...(cur.splitExpenses ?? []));
            return acc;
          }, []),
          ...(
            evaluationResponse.portExpensesSection?.expenseEvaluations ?? []
          ).reduce<ExpenseEvaluationResponse[]>((acc, cur) => {
            acc.push(...(cur.editExpenses ?? []), ...(cur.splitExpenses ?? []));
            return acc;
          }, []),
          ...(
            evaluationResponse.husbandryExpensesSection?.expenseEvaluations ??
            []
          ).reduce<ExpenseEvaluationResponse[]>((acc, cur) => {
            acc.push(...(cur.editExpenses ?? []), ...(cur.splitExpenses ?? []));
            return acc;
          }, [])
        ];

        const expenseEvaluationEntityState =
          expenseEvaluationEntityAdapter.setAll(
            expenseEvaluationEntityAdapter.getInitialState(),
            expenseEvaluations
          );

        return {
          ...evaluationResponse,
          evaluationExpensesEntities: expenseEvaluationEntityState
        };
      },
      providesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),
    markAsDeletedEvaluation: build.mutation<
      EvaluationResponse,
      { evaluationId: number | string }
    >({
      query: ({ evaluationId }) => ({
        url: `/mark-as-deleted`,
        data: evaluationId,
        method: 'POST'
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),
    bulkApproveEvaluationExpenses: build.mutation<
      EvaluationResponse,
      {
        id: string;
        expenseIds: number[];
        accountingCodeId: string | null;
        comments: string | null;
      }
    >({
      query: (data) => ({
        url: `/bulk-approve-expenses`,
        method: 'POST',
        data
      }),
      invalidatesTags: (res, _err, { id }) =>
        res ? [{ type: 'EVALUATION', id }] : ['EVALUATION']
    }),
    bulkSplitEvaluationExpenses: build.mutation<
      EvaluationResponse,
      {
        id: string;
        expenseIds: string[];
        departmentId: string;
        accountingCodeId: string;
      }
    >({
      query: (data) => ({
        url: `/bulk-split-expenses`,
        method: 'POST',
        data
      }),
      invalidatesTags: (res, _err, { id }) =>
        res ? [{ type: 'EVALUATION', id }] : ['EVALUATION']
    }),
    bulkApproveSplitEvaluationExpenses: build.mutation<
      EvaluationResponse,
      { evaluationId: string | number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/bulk-approve-pending-split-expenses`,
        method: 'POST'
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', evaluationId }] : ['EVALUATION']
    }),
    resetEvaluation: build.mutation<
      EvaluationResponse,
      { evaluationId: string | number }
    >({
      query: ({ evaluationId }) => ({
        url: `/reset`,
        method: 'POST',
        data: evaluationId
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res
          ? [
              { type: 'EVALUATION', evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    }),
    savePaymentDueDate: build.mutation<
      EvaluationResponse,
      {
        id: string;
        dueDate?: string | null;
        timeZone?: string | null;
      }
    >({
      query: (data) => ({
        url: `/save-payment-due-date`,
        method: 'POST',
        data
      }),
      invalidatesTags: (res, _err, { id }) =>
        res ? [{ type: 'EVALUATION', id }] : ['EVALUATION']
    }),
    saveExpenseEvaluationParameters: build.mutation<
      ExpenseEvaluationResponse,
      { form: ExpenseEvaluationParametersForm; evaluationId: string | number }
    >({
      query: ({ form, evaluationId }) => ({
        url: `/${evaluationId}/save-expense-evaluation-parameters`,
        method: 'POST',
        data: form
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', evaluationId }] : ['EVALUATION']
    }),
    restoreEvaluationExpense: build.mutation<
      ExpenseEvaluationResponse,
      { expense: ExpenseEvaluationResponse }
    >({
      query: ({ expense }) => ({
        url: `/${expense.evaluationId}/restore-expense-evaluation`,
        method: 'POST',
        data: expense.id
      }),
      invalidatesTags: (res, _err, { expense }) =>
        res
          ? [
              { type: 'EVALUATION', id: expense.evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    }),
    splitExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        payload: {
          id: number;
          departmentId: number;
          accountingCodeId: number;
          amount: number;
        };
      }
    >({
      query: ({ payload, evaluationId }) => {
        const data = PsUtilities.createFormRequestParts(payload, undefined);

        return {
          url: `/${evaluationId}/split-expense-evaluation`,
          method: 'POST',
          data
        };
      },
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),
    editExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      { evaluationId: string | number; form: ExpenseEvaluationEditForm }
    >({
      query: ({ evaluationId, form }) => ({
        method: 'POST',
        url: `/${evaluationId}/edit-expense-evaluation`,
        data: form
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),
    deleteChildExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      { childExpense: ExpenseEvaluationResponse }
    >({
      query: ({ childExpense }) => ({
        method: 'POST',
        url: `/${childExpense.evaluationId}/delete-child-expense-evaluation`,
        data: childExpense.id
      }),
      invalidatesTags: (res, _err, { childExpense }) =>
        res
          ? [
              { type: 'EVALUATION', id: childExpense.evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    }),
    backForReviewExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        id: string;
        comments: string;
      }
    >({
      query: ({ evaluationId, id, comments }) => ({
        url: `/${evaluationId}/back-for-review-expense-evaluation`,
        method: 'POST',
        data: { id, comments }
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),
    divideSplitExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        formData: {
          id: string;
          parentId: string;
          selections: { accountingCodeId: string; amount: number }[];
        };
      }
    >({
      query: ({ evaluationId, formData }) => ({
        url: `/${evaluationId}/divide-split-expense-evaluation`,
        method: 'POST',
        data: formData
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),
    reAllocateSplitExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        formData: {
          splitExpenseEvaluationId: string;
          department: {
            id: string;
            name: string;
            abbreviation: string;
          };
          accountingCodeId: string;
        };
      }
    >({
      query: ({ evaluationId, formData }) => ({
        url: `/${evaluationId}/reallocate-split-expense-evaluation`,
        method: 'POST',
        data: formData
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res
          ? [
              { type: 'EVALUATION', id: evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    }),

    submitChartererAccountReport: build.mutation<
      EvaluationResponse,
      { evaluationId: string | number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/submit-charterer-account-report`,
        method: 'GET'
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),
    loadAvailableChartererAccountExpenses: build.query<
      EvaluationAvailableChartererAccountItemsOverviewResponse,
      { evaluationId: string | number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/load-available-charterer-account-expenses`,
        method: 'GET'
      })
    }),
    revertExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        expenseId: string | number;
      }
    >({
      query: ({ evaluationId, expenseId }) => ({
        url: `/${evaluationId}/revert-expense-evaluation`,
        method: 'POST',
        data: expenseId
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res
          ? [
              { type: 'EVALUATION', id: evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    })
  })
});

export type StateWithEvaluationApi = {
  [evaluationApi.reducerPath]: ReturnType<typeof evaluationApi.reducer>;
};

export const {
  useVerifyExpenseEvaluationMutation,
  useFetchLinkedPDAsQuery,
  useSubmitLinkedPDAsMutation,
  useFetchBudgetStatusQuery,
  usePerformBudgetCheckMutation,
  usePerformRetryTaskMutation,
  usePerformGetTaskQuery,
  useSavePaymentInstructionsMutation,
  useSubmitAprMutation,
  useFetchBudgetCommitStatusQuery,
  usePerformBudgetCommitMutation,
  useSendBudgetCommitReminderMutation,
  useLoadEvaluationQuery,
  useMarkAsDeletedEvaluationMutation,
  useBulkApproveEvaluationExpensesMutation,
  useBulkSplitEvaluationExpensesMutation,
  useBulkApproveSplitEvaluationExpensesMutation,
  useResetEvaluationMutation,
  useSavePaymentDueDateMutation,
  useSaveExpenseEvaluationParametersMutation,
  useRestoreEvaluationExpenseMutation,
  useSplitExpenseEvaluationMutation,
  useEditExpenseEvaluationMutation,
  useDeleteChildExpenseEvaluationMutation,
  useBackForReviewExpenseEvaluationMutation,
  useDivideSplitExpenseEvaluationMutation,
  useReAllocateSplitExpenseEvaluationMutation,
  useSubmitChartererAccountReportMutation,
  useLazyLoadAvailableChartererAccountExpensesQuery,
  useRevertExpenseEvaluationMutation
} = evaluationApi;

export const invalidateEvaluation = (evaluationId: string | number) =>
  evaluationApi.util.invalidateTags([{ type: 'EVALUATION', id: evaluationId }]);

export type fetchBudgetStatusResponse =
  typeof evaluationApi.endpoints.fetchBudgetStatus.Types.ResultType;

export const invalidateBudgetStatus = evaluationApi.util.invalidateTags([
  'BudgetStatus',
  'BudgetCommit'
]);
